import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Card, Button } from '../components';
import { VacancyApplicationModal } from '../components/VacancyApplicationModal';
import { useApplyVacancy } from '../hooks/useVacancies';
import {
  useNegotiateOpdracht,
  useApproveChange,
  useCompleteOpdracht,
  useApproveCompletion,
  useRejectOpdracht,
  useApproveRejection,
  useEmployerNegotiateOpdracht,
  useEmployerApproveChange,
  useEmployerCompleteOpdracht,
  useEmployerApproveCompletion,
  useEmployerRejectOpdracht,
  useEmployerApproveRejection
} from '../hooks/useOpdrachten';
import { useAuthStore } from '../store/authStore';
import { ArrowLeft, Briefcase, MapPin, Clock, Euro, Edit, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { useTranslation } from 'react-i18next';
import { MIN_HOURLY_RATE } from '../config/rateConstants';

export const VacancyDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const { t } = useTranslation('employer');
  const [showNegotiateModal, setShowNegotiateModal] = useState(false);
  const [negotiateChanges, setNegotiateChanges] = useState('');
  const [negotiateHourlyRate, setNegotiateHourlyRate] = useState<string>('');
  const [showApplicationModal, setShowApplicationModal] = useState(false);

  const { data: vacancy, isLoading } = useQuery({
    queryKey: ['vacancy-detail', id, user?.role],
    queryFn: async () => {
      if (!id) return null;
      // Professional uses /professional/vacancies/:id
      if (user?.role === 'professional') {
        const res = await api.get(`/professional/vacancies/${id}`);
        return res.data?.data || null;
      }
      // Employer uses /employer/opdrachten/:id
      if (user?.role === 'opdrachtgever') {
        const res = await api.get(`/employer/opdrachten/${id}`);
        return res.data?.data || null;
      }
      return null;
    },
    enabled: !!id && !!user?.role,
  });
  const queryClient = useQueryClient();
  const applyMutation = useApplyVacancy();

  // Get opdracht status if it's an accepted opdracht
  const { data: opdracht, isLoading: isLoadingOpdracht } = useQuery({
    queryKey: ['opdracht', id, user?.id],
    queryFn: async () => {
      if (!user || !id) return null;
      try {
        if (user.role === 'professional') {
          const res = await api.get('/professional/opdrachten', { params: { status: undefined } });
          const opdrachten = res.data.data || [];
          return opdrachten.find((o: any) => (o.vacancy_id || o.id) === id);
        } else if (user.role === 'opdrachtgever') {
          const res = await api.get('/employer/opdrachten', { params: { status: undefined } });
          const opdrachten = res.data.data || [];
          return opdrachten.find((o: any) => (o.vacancy_id || o.id) === id);
        }
      } catch {
        return null;
      }
      return null;
    },
    enabled: !!user && !!id,
  });

  // Hooks altijd allebei aanroepen (vaste hook-volgorde); daarna per rol kiezen.
  const isProfessional = user?.role === 'professional';
  const negotiateProf = useNegotiateOpdracht();
  const negotiateEmp = useEmployerNegotiateOpdracht();
  const approveChangeProf = useApproveChange();
  const approveChangeEmp = useEmployerApproveChange();
  const completeProf = useCompleteOpdracht();
  const completeEmp = useEmployerCompleteOpdracht();
  const approveCompletionProf = useApproveCompletion();
  const approveCompletionEmp = useEmployerApproveCompletion();
  const rejectProf = useRejectOpdracht();
  const rejectEmp = useEmployerRejectOpdracht();
  const approveRejectionProf = useApproveRejection();
  const approveRejectionEmp = useEmployerApproveRejection();

  const negotiateMutation = isProfessional ? negotiateProf : negotiateEmp;
  const approveChangeMutation = isProfessional ? approveChangeProf : approveChangeEmp;
  const completeMutation = isProfessional ? completeProf : completeEmp;
  const approveCompletionMutation = isProfessional ? approveCompletionProf : approveCompletionEmp;
  const rejectMutation = isProfessional ? rejectProf : rejectEmp;
  const approveRejectionMutation = isProfessional ? approveRejectionProf : approveRejectionEmp;

  // Fetch professional's own profile types (to check eligibility)
  const { data: professionalProfile } = useQuery({
    queryKey: ['professional-profile-types', user?.id],
    queryFn: async () => {
      const res = await api.get('/professional/profile');
      return res.data?.data || null;
    },
    enabled: user?.role === 'professional',
  });

  const applicationStatus =
    (vacancy as any)?.application_status ??
    (opdracht as any)?.application_status ??
    null;

  // Check if professional's type matches vacancy requirement
  const vacancyType = (vacancy as any)?.professional_type;
  const professionalTypes: string[] = (() => {
    const raw = professionalProfile?.professional_types;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try { return JSON.parse(raw); } catch { return []; }
  })();
  const typeMatches = !vacancyType || professionalTypes.length === 0 || professionalTypes.includes(vacancyType);

  // Is this an open vacancy (no specific target) that a professional can apply to?
  const isOpenVacancy = user?.role === 'professional' && !(vacancy as any)?.target_professional_id;
  const isIngediend = applicationStatus === 'ingediend';
  const isAccepted = applicationStatus === 'geaccepteerd' || applicationStatus === 'onderhandelingen';
  const isNegotiating = applicationStatus === 'onderhandelingen';
  const canApproveChange = isNegotiating && opdracht?.change_proposed_by !== user?.id;
  const canApproveCompletion = opdracht?.completion_requested && opdracht?.completion_requested_by !== user?.id;
  const canApproveRejection = opdracht?.rejection_requested && opdracht?.rejection_requested_by !== user?.id;

  const currentProposalText = (() => {
    const proposed = (opdracht as any)?.proposed_changes;
    if (!proposed) return '';
    if (typeof proposed === 'string') return proposed;
    if (typeof proposed?.description === 'string') return proposed.description;
    if (typeof proposed?.message === 'string') return proposed.message;
    try {
      return JSON.stringify(proposed, null, 2);
    } catch {
      return '';
    }
  })();

  const currentProposalHourlyRate = (() => {
    const proposed = (opdracht as any)?.proposed_changes;
    if (!proposed) return null;
    const raw = (typeof proposed === 'string') ? (() => { try { return JSON.parse(proposed); } catch { return null; } })() : proposed;
    if (!raw) return null;
    const v = raw.hourly_rate ?? raw.hourlyRate ?? null;
    if (v === null || v === undefined || String(v).trim() === '') return null;
    const n = parseFloat(String(v));
    return Number.isFinite(n) ? n : null;
  })();

  const agreedHourlyRate =
    (opdracht as any)?.agreed_hourly_rate ??
    (opdracht as any)?.agreedHourlyRate ??
    null;

  if (!id) return null;

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-10">
        <Card>
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">{t('vacancyDetail.loading')}</p>
          </div>
        </Card>
      </div>
    );
  }

  if (!vacancy) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-10">
        <Card>
          <p className="text-sm text-gray-700">{t('vacancyDetail.notFound')}</p>
        </Card>
      </div>
    );
  }

  const canApply = user?.role === 'professional';
  // Targeted-vacancy accept button (legacy direct flow)
  const isTargetedAtMe = (vacancy as any)?.target_professional_id === user?.id;
  const canShowApplyButton = user?.role === 'professional' && !isOpenVacancy && isTargetedAtMe && !isAccepted && !isLoadingOpdracht && !isIngediend;
  // Open vacancy react button
  const canShowReagerenButton = isOpenVacancy && !isAccepted && !isIngediend && !isLoadingOpdracht;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('vacancyDetail.back')}
        </Button>
      </div>

      <Card>
        <div className="p-6">
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">{vacancy.titel}</h1>
              {vacancy.employer_company_name && (
                <p className="mt-1 text-sm text-gray-600">{vacancy.employer_company_name}</p>
              )}
            </div>

            <div className="flex-shrink-0 flex flex-col gap-2">
              {/* Open vacancy: Reageren button */}
              {canShowReagerenButton && (
                <Button onClick={() => setShowApplicationModal(true)}>
                  Reageren
                </Button>
              )}

              {/* Open vacancy: already submitted */}
              {isOpenVacancy && isIngediend && (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  Sollicitatie ingediend
                </div>
              )}

              {/* Prevent "Accepteren" flash/click before we know application status */}
              {user?.role === 'professional' && !isOpenVacancy && !isAccepted && isLoadingOpdracht && (
                <Button disabled loading>
                  {t('vacancyDetail.loadingBtn')}
                </Button>
              )}

              {canShowApplyButton && !typeMatches && (
                <div className="flex flex-col gap-2 max-w-xs">
                  <Button disabled>
                    {t('vacancyDetail.accept')}
                  </Button>
                  <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      Je bent niet ingeschreven als <strong>{vacancyType}</strong>.{' '}
                      <Link to="/employer/opleidingen" className="underline font-medium">
                        Schrijf je in voor een opleiding
                      </Link>{' '}
                      om te kunnen reageren.
                    </span>
                  </div>
                </div>
              )}

              {canShowApplyButton && typeMatches && (
                <Button
                  disabled={!canApply}
                  loading={applyMutation.isPending}
                  onClick={() => {
                    if (!canApply) return;
                    const motivation = window.prompt(t('vacancyDetail.motivationPrompt'));
                    applyMutation.mutate(
                      { id, motivation: motivation || undefined },
                      {
                        onSuccess: (res: any) => {
                          const dossierId = res?.data?.data?.dossier_id;
                          if (dossierId) navigate(`/professional/dossiers/${dossierId}`);
                        }
                      }
                    );
                  }}
                >
                  {t('vacancyDetail.accept')}
                </Button>
              )}

              {isAccepted && (
                <>
                  {canApproveChange && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (user?.role === 'professional') {
                          approveChangeMutation.mutate(id as any);
                        } else {
                          approveChangeMutation.mutate({ id, professionalId: opdracht?.professional_id } as any);
                        }
                      }}
                    >
                      {t('vacancyDetail.approveChange')}
                    </Button>
                  )}

                  <Button
                    variant="outline"
                    onClick={() => {
                      // Allow (re)submitting a proposal even while already in 'onderhandelingen'
                      setNegotiateChanges(currentProposalText || '');
                      setNegotiateHourlyRate(
                        currentProposalHourlyRate !== null && currentProposalHourlyRate !== undefined
                          ? String(currentProposalHourlyRate)
                          : (agreedHourlyRate !== null && agreedHourlyRate !== undefined ? String(agreedHourlyRate) : '')
                      );
                      setShowNegotiateModal(true);
                    }}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    {isNegotiating ? t('vacancyDetail.adjustProposal') : t('vacancyDetail.negotiate')}
                  </Button>

                  {canApproveCompletion && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (user?.role === 'professional') {
                          approveCompletionMutation.mutate(id as any);
                        } else {
                          approveCompletionMutation.mutate({ id, professionalId: opdracht?.professional_id } as any);
                        }
                      }}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {t('vacancyDetail.approveCompletion')}
                    </Button>
                  )}

                  {!opdracht?.completion_requested && !opdracht?.rejection_requested && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => {
                          window.prompt(t('vacancyDetail.completionReasonPrompt'));
                          if (user?.role === 'professional') {
                            completeMutation.mutate(id as any);
                          } else {
                            completeMutation.mutate({ id, professionalId: opdracht?.professional_id } as any);
                          }
                        }}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {t('vacancyDetail.complete')}
                      </Button>

                      <Button
                        variant="outline"
                        onClick={() => {
                          const reason = window.prompt(t('vacancyDetail.rejectReasonPrompt'));
                          if (user?.role === 'professional') {
                            rejectMutation.mutate({ id, reason } as any);
                          } else {
                            rejectMutation.mutate({ id, professionalId: opdracht?.professional_id, reason } as any);
                          }
                        }}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        {t('vacancyDetail.reject')}
                      </Button>
                    </>
                  )}

                  {canApproveRejection && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (user?.role === 'professional') {
                          approveRejectionMutation.mutate(id as any);
                        } else {
                          approveRejectionMutation.mutate({ id, professionalId: opdracht?.professional_id } as any);
                        }
                      }}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      {t('vacancyDetail.approveRejection')}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="mt-6 prose max-w-none">
            <p className="text-gray-700 whitespace-pre-wrap">{vacancy.beschrijving}</p>
          </div>

          <div className="mt-6 flex flex-wrap gap-4 text-sm text-gray-600">
            <div className="flex items-center">
              <Briefcase className="w-4 h-4 mr-2" />
              {vacancy.professional_type}
            </div>
            {Array.isArray(vacancy.active_regions) && vacancy.active_regions.length > 0 && (
              <div className="flex items-center">
                <MapPin className="w-4 h-4 mr-2" />
                {vacancy.active_regions.join(', ')}
              </div>
            )}
            {vacancy.work_preference && (
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                {vacancy.work_preference}
              </div>
            )}
            {vacancy.hourly_rate_max && (
              <div className="flex items-center">
                <Euro className="w-4 h-4 mr-2" />
                {t('vacancyDetail.maxRate', { rate: vacancy.hourly_rate_max })}
              </div>
            )}
          </div>

          {/* Status badge */}
          {applicationStatus && (
            <div className="mt-4">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                applicationStatus === 'nieuw' ? 'bg-blue-100 text-blue-800' :
                applicationStatus === 'ingediend' ? 'bg-blue-100 text-blue-800' :
                applicationStatus === 'geaccepteerd' ? 'bg-green-100 text-green-800' :
                applicationStatus === 'onderhandelingen' ? 'bg-yellow-100 text-yellow-800' :
                applicationStatus === 'afgerond' ? 'bg-gray-100 text-gray-800' :
                applicationStatus === 'afgewezen' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {t('vacancyDetail.statusLabel')}: {applicationStatus === 'ingediend' ? 'Sollicitatie ingediend' : applicationStatus}
              </span>
            </div>
          )}

          {/* Current proposal (when negotiating) */}
          {isNegotiating && (currentProposalText || currentProposalHourlyRate !== null) && (
            <div className="mt-4 p-4 rounded-lg bg-yellow-50 border border-yellow-200">
              <div className="text-sm font-medium text-yellow-900 mb-2">{t('vacancyDetail.currentProposal')}</div>
              {currentProposalHourlyRate !== null && (
                <div className="text-sm text-yellow-900 mb-2">
                  {t('vacancyDetail.hourlyRateProposal', { rate: currentProposalHourlyRate.toFixed(2) })}
                </div>
              )}
              <div className="text-sm text-yellow-900 whitespace-pre-wrap">{currentProposalText}</div>
            </div>
          )}

          {agreedHourlyRate !== null && agreedHourlyRate !== undefined && (
            <div className="mt-4 p-4 rounded-lg bg-gray-50 border border-gray-200">
              <div className="text-sm font-medium text-gray-900 mb-1">{t('vacancyDetail.agreedHourlyRate')}</div>
              <div className="text-sm text-gray-800">€{parseFloat(String(agreedHourlyRate)).toFixed(2)}/uur</div>
            </div>
          )}
        </div>
      </Card>

      {/* Reageren Modal */}
      {showApplicationModal && vacancy && (
        <VacancyApplicationModal
          vacancy={{ id: id!, titel: (vacancy as any).titel, hourly_rate_max: (vacancy as any).hourly_rate_max }}
          onClose={() => setShowApplicationModal(false)}
          onSuccess={() => {
            setShowApplicationModal(false);
            queryClient.invalidateQueries({ queryKey: ['vacancy-detail', id] });
            queryClient.invalidateQueries({ queryKey: ['vacancies'] });
          }}
        />
      )}

      {/* Negotiate Modal */}
      {showNegotiateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="max-w-md w-full mx-4">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">
                {isNegotiating ? t('vacancyDetail.adjustProposal') : t('vacancyDetail.proposeChanges')}
              </h2>

              <label className="block text-sm font-medium text-gray-700 mb-1">{t('vacancyDetail.hourlyRateLabel')}</label>
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                min={MIN_HOURLY_RATE}
                value={negotiateHourlyRate}
                onChange={(e) => setNegotiateHourlyRate(e.target.value)}
                placeholder={t('vacancyDetail.hourlyRatePlaceholder')}
                className="w-full p-3 border border-gray-300 rounded-lg mb-4"
              />

              <textarea
                value={negotiateChanges}
                onChange={(e) => setNegotiateChanges(e.target.value)}
                placeholder={t('vacancyDetail.changesPlaceholder')}
                className="w-full p-3 border border-gray-300 rounded-lg mb-4"
                rows={6}
              />
              <div className="flex space-x-2">
                <Button
                  onClick={() => {
                    const hourlyRateValue =
                      negotiateHourlyRate && String(negotiateHourlyRate).trim() !== ''
                        ? parseFloat(String(negotiateHourlyRate))
                        : null;
                    const changes: any = { description: negotiateChanges };
                    if (hourlyRateValue !== null && Number.isFinite(hourlyRateValue)) {
                      changes.hourly_rate = hourlyRateValue;
                    }
                    if (user?.role === 'professional') {
                      negotiateMutation.mutate({ id, changes } as any);
                    } else {
                      negotiateMutation.mutate({ id, professionalId: opdracht?.professional_id, changes } as any);
                    }
                    setShowNegotiateModal(false);
                    setNegotiateChanges('');
                    setNegotiateHourlyRate('');
                  }}
                >
                  {isNegotiating ? t('vacancyDetail.save') : t('vacancyDetail.propose')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowNegotiateModal(false);
                    setNegotiateChanges('');
                    setNegotiateHourlyRate('');
                  }}
                >
                  {t('vacancyDetail.cancel')}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};


